<template>
    <div
        class="mag-toast-container"
        popover="manual"
        @vue:mounted="
            (e: VNode) => {
                e.el?.showPopover();
            }
        "
    >
        <template v-for="toast in toasts" :key="toast.key">
            <MagTransition
                name="mag-toast"
                appear
                :duration="300"
                @leave="removeToast(toast.key)"
            >
                <div class="mag-toast" v-if="!toast.closing">
                    <component
                        :is="toast.component"
                        @close="toast.closing = true"
                    />
                </div>
            </MagTransition>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { useToasts } from "@/composables/mag-toast";
import { type VNode } from "vue";

const toasts = useToasts();

const removeToast = (key: string) => {
    toasts.value = toasts.value.filter((t) => t.key !== key);
};
</script>

<style lang="scss" scoped>
.mag-toast-enter-active,
.mag-toast-leave-active {
    transition:
        opacity 0.3s,
        transform 0.3s,
        max-height 0.3s;
}

.mag-toast-enter-from {
    opacity: 0;
    transform: translateX(-250px);
}

.mag-toast-leave-to {
    opacity: 0;
    transform: scaleY(0);
    max-height: 0;
}

.mag-toast-container {
    position: fixed;
    left: 16px;
    right: auto;
    top: auto;
    width: 250px;
    bottom: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    pointer-events: none;
    transition: opacity 0.3s;
    transform-origin: bottom center;
    background: none;
    border: 0;

    @media (prefers-reduced-motion: no-preference) {
        & {
            transition:
                opacity 0.3s,
                padding-left 0.3s;
        }
    }

    body:has(.mag-navbar:hover) & {
        padding-left: 200px;
        opacity: 0.2;
    }

    @media (max-width: 700px) {
        & {
            bottom: 112px;
            padding: 0 16px;
        }
    }

    @media (max-width: 500px) {
        & {
            bottom: calc(env(safe-area-inset-bottom, 0px) + 92px);
            padding: 0 8px;
        }
    }

    .mag-toast {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
        border-radius: 8px;
        background: var(--blurBg);
        border: 1px solid var(--accentedBg);
        backdrop-filter: var(--blur, blur(12px));
        overflow: hidden;
        text-overflow: ellipsis;

        :where(&) {
            max-height: 200px;
        }

        @media (prefers-reduced-transparency: reduce) {
            & {
                background: var(--bg);
                backdrop-filter: none;
            }
        }

        > :deep(.mag-toast-string) {
            padding: 12px 16px;
            white-space: preserve-breaks;
        }
    }
}
</style>
